import { useGlossaryQuery } from '@/hooks/query-hooks';
import { Box, Grid, IconButton, Link, List, ListItem, ListItemButton, ListItemText, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { UpdateGlossaryTermModal } from './UpdateGlossaryModal';
import { DefaultContainer } from '@/components/DefaultContainer';
import { CreateGlossaryTermDTO, GlossaryTermDTO } from '@/generated';
import { useContext, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ImageFromServer } from '@/components/images/ImageFromServer';
import { useDeleteGlossaryTerm } from '@/hooks/glossary-mutation.hooks';
import { DeleteDialog } from '@/components/DeleteDialog';
import { isAdminUser } from '@/utils/user';
import { AppContext } from '@/contexts/app.context';
import { ImageDialog } from '@/components/ImageDialog';
import { useParams } from 'react-router-dom';

export const Glossary = () => {
  const { glossaryTerms } = useGlossaryQuery();
  const { glossaryTermId } = useParams();
  const [editGlossaryTerm, setEditGlossaryTerm] = useState<GlossaryTermDTO | CreateGlossaryTermDTO>();
  const [selectedGlossaryTerm, setSelectedGlossaryTerm] = useState<GlossaryTermDTO>();
  const [showDeleteGlossaryTermDialog, setShowDeleteGlossaryTermDialog] = useState<boolean>();
  const deleteGlossaryTermMutation = useDeleteGlossaryTerm();
  const [modalImageId, setModalImageId] = useState<string>('');
  const context = useContext(AppContext);
  const localUser = context.state?.userProfile;

  useEffect(() => {
    //Created, selected, aborted //Maybe a bit unclear in this func.
    if (glossaryTermId && !selectedGlossaryTerm) {
      const gt = glossaryTerms?.find((g) => g.id === glossaryTermId);
      if (gt) setSelectedGlossaryTerm(gt);
    }
    if (selectedGlossaryTerm) {
      const gt = glossaryTerms?.find((g) => g.id === selectedGlossaryTerm.id);
      if (gt) setSelectedGlossaryTerm(gt);
    }
  }, [glossaryTerms, glossaryTermId]);

  return (
    <DefaultContainer>
      <DeleteDialog
        title="Delete entry?"
        text="Do you really want to delete this glossary term?"
        open={showDeleteGlossaryTermDialog ?? false}
        handleClose={async (deleted) => {
          if (selectedGlossaryTerm && deleted) {
            await deleteGlossaryTermMutation.mutateAsync(selectedGlossaryTerm?.id);
            setSelectedGlossaryTerm(undefined);
          }
          setShowDeleteGlossaryTermDialog(false);
        }}
      />
      <ImageDialog
        imageId={modalImageId}
        open={modalImageId !== ''}
        onClose={() => {
          setModalImageId('');
        }}
      />
      <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
        <Grid sx={{ alignContent: 'center', alignItems: 'center' }} container gap={2} direction={'row'}>
          {isAdminUser(localUser) && (
            <>
              <Grid>
                <Tooltip title={'Create New Glossary Term'}>
                  <IconButton
                    onClick={() => {
                      setEditGlossaryTerm({ description: '', name: '', links: [] });
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid>
                <Tooltip title={'Edit selected Glossary Term'}>
                  <IconButton
                    disabled={!selectedGlossaryTerm}
                    onClick={() => {
                      if (selectedGlossaryTerm) setEditGlossaryTerm(selectedGlossaryTerm);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid>
                <Tooltip title={'Delete selected Glossary Term'}>
                  <IconButton
                    disabled={!selectedGlossaryTerm}
                    onClick={() => {
                      setShowDeleteGlossaryTermDialog(true);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
      <UpdateGlossaryTermModal
        editGlossaryTerm={editGlossaryTerm}
        setEditGlossaryTerm={(gt) => {
          setEditGlossaryTerm(gt);
        }}
      />
      <Grid spacing={1} sx={{ my: 1 }} container>
        <Grid size={{ xs: 12, md: 2 }}>
          <List sx={{}}>
            {glossaryTerms?.map((gt, i) => (
              <ListItem key={i} component="div" disablePadding>
                <ListItemButton
                  onClick={() => {
                    setSelectedGlossaryTerm(gt);
                  }}
                >
                  <ListItemText primary={`${gt.name}`} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Grid>
        <Grid size={{ xs: 12, md: 10 }} container>
          <Grid size={5}>
            <Typography sx={{ mb: 1 }} variant="h5">
              {selectedGlossaryTerm?.name}
            </Typography>
            <Typography>{selectedGlossaryTerm?.description}</Typography>
          </Grid>
          <Grid size={7}>
            {selectedGlossaryTerm?.imageId ? (
              <ImageFromServer
                sx={{
                  width: '100%',
                  height: 500,
                  objectFit: 'contain',
                  display: 'block',
                  borderRadius: 2,
                }}
                id={selectedGlossaryTerm?.imageId}
                thumbnail
                alt=""
              />
            ) : (
              <></>
            )}
          </Grid>
          <Grid size={6}>
            <GlossaryLinks glossaryTerm={selectedGlossaryTerm} />
          </Grid>
          <Grid size={6}>
            {selectedGlossaryTerm?.additionalImageIds?.length ? (
              <>
                <Typography variant="h6">Additional Images</Typography>
                <Stack direction={'row'} sx={{ gap: 1, my: 1, flexWrap: 'wrap' }}>
                  {selectedGlossaryTerm?.additionalImageIds?.map((id) => (
                    <ImageFromServer onClick={() => setModalImageId(id)} sx={{ width: 50, height: 50 }} thumbnail={false} alt="image" id={id} />
                  ))}
                </Stack>
              </>
            ) : (
              <></>
            )}
          </Grid>
        </Grid>
      </Grid>
    </DefaultContainer>
  );
};

const GlossaryLinks = (props: { glossaryTerm?: GlossaryTermDTO }) => {
  const { glossaryTerm } = props;
  return (
    <Stack>
      <Typography variant="h6">Links:</Typography>
      {glossaryTerm?.links.map((el) => (
        <Box>
          <Link onClick={(e) => e.stopPropagation()} href={el.uri}>
            {el.title}
          </Link>
        </Box>
      ))}
    </Stack>
  );
};
